import { useState } from "react";
import { useTranslation } from "react-i18next";
import PageHeader from "../components/PageHeader";
import { supabase } from "../supabaseClient";

const WA_NUMBER = "212629291310";
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID?.trim();
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID?.trim();
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY?.trim();
const SUPABASE_KEY_AVAILABLE =
  process.env.REACT_APP_SUPABASE_ANON_KEY ||
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
const PHONE_REGEX = /^\+?[0-9\s()-]{8,20}$/;

const ContactPage = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name:"", email:"", phone:"", service:"", message:"", guests:"2" });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  const isValidEmail = (value) => EMAIL_REGEX.test(value.trim());
  const isValidPhone = (value) => !value.trim() || PHONE_REGEX.test(value.trim());

  const buildWAMessage = () => {
    const msg =
      `Anassik Travel Inquiry\n\n` +
      `${t("contact.whatsAppName")}: ${form.name}\n` +
      `${t("contact.whatsAppPhone")}: ${form.phone || "-"}\n` +
      `${t("contact.whatsAppEmail")}: ${form.email}\n` +
      `${t("contact.whatsAppService")}: ${form.service || "-"}\n` +
      `${t("contact.whatsAppGuests")}: ${form.guests}\n\n` +
      `${t("contact.whatsAppMessage")}:\n${form.message}`;
    return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(msg)}`;
  };

  const saveToSupabase = async () => {
    if (!process.env.REACT_APP_SUPABASE_URL || !SUPABASE_KEY_AVAILABLE) {
      throw new Error("Supabase is not configured.");
    }

    const { error } = await supabase.from("contact_submissions").insert([{
      name: form.name,
      email: form.email,
      phone: form.phone,
      service: form.service,
      guests: form.guests,
      message: form.message,
    }]);

    if (error) {
      throw error;
    }
  };

  const sendEmail = async () => {
    if (!EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID || !EMAILJS_PUBLIC_KEY) {
      throw new Error("EmailJS is not configured.");
    }

    const response = await fetch("https://api.emailjs.com/api/v1.0/email/send", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        service_id: EMAILJS_SERVICE_ID,
        template_id: EMAILJS_TEMPLATE_ID,
        user_id: EMAILJS_PUBLIC_KEY,
        template_params: {
          from_name:  form.name,
          from_email: form.email,
          phone:      form.phone,
          service:    form.service,
          guests:     form.guests,
          message:    form.message,
          to_email:   "contact@anassiktravel.com",
        },
      }),
    });

    if (!response.ok) {
      const details = await response.text();
      throw new Error(`EmailJS request failed: ${details}`);
    }
  };

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError(t("contact.errorMsg"));
      return;
    }
    if (!isValidEmail(form.email)) {
      setError(t("contact.invalidEmail"));
      return;
    }
    if (!isValidPhone(form.phone)) {
      setError(t("contact.invalidPhone"));
      return;
    }
    setError("");
    setSending(true);

    const results = await Promise.allSettled([
      sendEmail(),
      saveToSupabase(),
    ]);

    setSending(false);

    const failures = results.filter((result) => result.status === "rejected");

    if (failures.length === results.length) {
      console.warn("Contact form delivery failed:", failures);
      setError(t("contact.submitFailed"));
      return;
    }

    if (failures.length > 0) {
      console.warn("Partial contact form delivery failure:", failures);
      setError(t("contact.partialSuccess"));
    } else {
      setError("");
    }

    setSent(true);
  };

  const handleWhatsApp = () => {
    if (!form.name.trim() || !form.message.trim()) {
      setError(t("contact.whatsAppError"));
      return;
    }
    if (form.email.trim() && !isValidEmail(form.email)) {
      setError(t("contact.invalidEmail"));
      return;
    }
    if (!isValidPhone(form.phone)) {
      setError(t("contact.invalidPhone"));
      return;
    }

    setError("");
    window.open(buildWAMessage(), "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <style>{`
        .contact-section { padding:60px 60px 80px; background:var(--cream); }
        .contact-grid { display:grid; grid-template-columns:1fr 1.4fr; gap:60px; align-items:start; }
        .contact-info { background:var(--deep); border-radius:16px; padding:40px; color:rgba(255,255,255,0.8); }
        .ci-item { display:flex; gap:16px; margin-bottom:28px; }
        .ci-icon { width:48px; height:48px; border-radius:10px; flex-shrink:0; background:rgba(201,168,76,0.15); border:1px solid rgba(201,168,76,0.2); display:flex; align-items:center; justify-content:center; font-size:20px; }
        .ci-label { font-size:11px; letter-spacing:2px; text-transform:uppercase; color:var(--gold); margin-bottom:6px; font-weight:600; }
        .ci-value { font-size:14px; line-height:1.6; }
        .map-embed { width:100%; height:240px; border-radius:12px; margin-top:24px; overflow:hidden; border:2px solid rgba(201,168,76,0.2); display:block; }
        .contact-form { background:white; border-radius:16px; padding:44px; box-shadow:0 8px 30px rgba(0,0,0,0.09); }
        .form-row { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
        .form-group { margin-bottom:20px; }
        .form-group label { display:block; font-size:12px; font-weight:600; letter-spacing:1.5px; text-transform:uppercase; color:var(--text-muted); margin-bottom:7px; }
        .form-group input, .form-group select, .form-group textarea {
          width:100%; padding:13px 16px; border:1.5px solid #E8E8E8; border-radius:8px;
          font-family:inherit; font-size:14px; color:var(--text);
          transition:border-color 0.2s; outline:none; resize:none; background:white;
        }
        .form-group input:focus, .form-group select:focus, .form-group textarea:focus { border-color:var(--gold); }
        .form-error { color:#e53935; font-size:13px; margin-bottom:16px; background:#fef2f2; padding:10px 14px; border-radius:6px; border:1px solid #fecaca; }
        .success-box { text-align:center; padding:48px 24px; }
        .wa-btn {
          display:flex; align-items:center; justify-content:center; gap:8px;
          width:100%; padding:13px; margin-top:10px;
          background:#25D366; color:white; border:none; border-radius:6px;
          font-size:14px; font-weight:600; cursor:pointer; font-family:inherit;
          transition:all 0.2s;
        }
        .wa-btn:hover { background:#1dba57; transform:translateY(-1px); }
        .sending-btn { opacity:0.7; cursor:not-allowed; }
        @media(max-width:768px) {
          .contact-section { padding:40px 20px 60px; }
          .contact-grid { grid-template-columns:1fr; }
          .contact-form { padding:24px; }
          .form-row { grid-template-columns:1fr; }
        }
      `}</style>

      <PageHeader
        tag={t("contact.headerTag")}
        title={t("contact.headerTitle")}
        subtitle={t("contact.headerSub")}
      />

      <div className="contact-section">
        <div className="contact-grid">

          {/* Info column */}
          <div>
            <h3 style={{fontFamily:"var(--font-heading)",fontSize:26,color:"var(--deep)",marginBottom:8}}>
              {t("contact.infoTitle")}
            </h3>
            <div className="gold-divider" />
            <p style={{color:"var(--text-muted)",fontSize:15,lineHeight:1.75,marginBottom:28}}>
              {t("contact.infoPara")}
            </p>
            <div className="contact-info">
              <div className="ci-item">
                <div className="ci-icon">📍</div>
                <div><div className="ci-label">{t("contact.address")}</div><div className="ci-value">217 Amgala, 2ème étage, Appt N3<br />Ain Chok, Casablanca, Maroc</div></div>
              </div>
              <div className="ci-item">
                <div className="ci-icon">📞</div>
                <div><div className="ci-label">{t("contact.phone")}</div><div className="ci-value">+212 665-009314<br />+212 522 528080</div></div>
              </div>
              <div className="ci-item">
                <div className="ci-icon">✉️</div>
                <div><div className="ci-label">{t("contact.email")}</div><div className="ci-value">contact@anassiktravel.com</div></div>
              </div>
              <div className="ci-item" style={{marginBottom:0}}>
                <div className="ci-icon">🕐</div>
                <div><div className="ci-label">{t("contact.hours")}</div><div className="ci-value">{t("contact.hoursVal")}</div></div>
              </div>
              <iframe className="map-embed" title="Anassik Travel Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3325.6397020994486!2d-7.5892669243770845!3d33.53675207335545!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xda633e50c8ea8b7%3A0xfd16b83b8797e960!2z2KfZhNmG2KfYs9mDINmE2YTYrdisINmI2KfZhNi52YXYsdipIEFOTkFTSUsgVFJBVkVM!5e0!3m2!1sen!2sma!4v1777051590475!5m2!1sen!2sma"
                style={{border:0}} allowFullScreen loading="lazy"
              />
            </div>
          </div>

          {/* Form column */}
          <div className="contact-form">
            {sent ? (
              <div className="success-box">
                {error && <div className="form-error">{error}</div>}
                <div style={{fontSize:64,marginBottom:20}}>✅</div>
                <h3 style={{fontFamily:"var(--font-heading)",fontSize:26,color:"var(--deep)",marginBottom:12}}>
                  {t("contact.successTitle")}
                </h3>
                <p style={{color:"var(--text-muted)",lineHeight:1.7,marginBottom:24}}>
                  {t("contact.successText")}
                </p>
                <button className="btn-gold" onClick={() => { setSent(false); setForm({name:"",email:"",phone:"",service:"",message:"",guests:"2"}); }}>
                  {t("contact.sendAnother")}
                </button>
                <button className="wa-btn" onClick={handleWhatsApp}>
                  {t("contact.whatsappBtn")}
                </button>
              </div>
            ) : (
              <>
                <h3 style={{fontFamily:"var(--font-heading)",fontSize:22,color:"var(--deep)",marginBottom:24}}>
                  {t("contact.formTitle")}
                </h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>{t("contact.name")}</label>
                    <input placeholder={t("contact.namePh")} value={form.name} onChange={update("name")} />
                  </div>
                  <div className="form-group">
                    <label>{t("contact.phoneLbl")}</label>
                    <input
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      placeholder={t("contact.phonePh")}
                      value={form.phone}
                      onChange={update("phone")}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>{t("contact.emailLbl")}</label>
                  <input
                    type="email"
                    autoComplete="email"
                    placeholder={t("contact.emailPh")}
                    value={form.email}
                    onChange={update("email")}
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>{t("contact.service")}</label>
                    <select value={form.service} onChange={update("service")}>
                      <option value="">{t("contact.selectService")}</option>
                      {t("contact.services", { returnObjects: true }).map(s => (
                        <option key={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{t("contact.guestsLbl")}</label>
                    <select value={form.guests} onChange={update("guests")}>
                      {[1,2,3,4,5,"6+"].map(n => <option key={n}>{n}</option>)}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>{t("contact.message")}</label>
                  <textarea rows={5} placeholder={t("contact.messagePh")} value={form.message} onChange={update("message")} />
                </div>
                {error && <div className="form-error">⚠️ {error}</div>}
                <button
                  className={`btn-gold ${sending ? "sending-btn" : ""}`}
                  style={{width:"100%",textAlign:"center"}}
                  onClick={handleSubmit}
                  disabled={sending}
                >
                  {sending ? "..." : t("contact.send")}
                </button>
                <button className="wa-btn" onClick={handleWhatsApp}>
                  {t("contact.whatsappBtn")}
                </button>
                <p style={{fontSize:12,color:"var(--text-muted)",marginTop:12,textAlign:"center"}}>
                  {t("contact.privacy")}
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ContactPage;
